/**
 * Bounded, browser-safe error identity for log payloads: a closed allowlist of error names (JS built-ins,
 * DOMException, this monorepo's own classes). `message` ships only via {@link errorMessageOf} (gated + capped),
 * `stack` never; an unlisted name collapses to a fallback, so a new error class must register here.
 * Message-only browser reports are classified by {@link messageOnlyErrorIdentityOf}; their text never ships.
 */
const ALLOWED_ERROR_NAMES = new Set([
	// JS built-ins
	"Error",
	"TypeError",
	"RangeError",
	"SyntaxError",
	"ReferenceError",
	"EvalError",
	"URIError",
	"AggregateError",
	// The standard DOMException name set (camera/WebAuthn/storage/abort paths)
	"AbortError",
	"ConstraintError",
	"DataCloneError",
	"DataError",
	"EncodingError",
	"HierarchyRequestError",
	"InUseAttributeError",
	"IndexSizeError",
	"InvalidAccessError",
	"InvalidCharacterError",
	"InvalidModificationError",
	"InvalidNodeTypeError",
	"InvalidStateError",
	"NamespaceError",
	"NetworkError",
	"NoModificationAllowedError",
	"NotAllowedError",
	"NotFoundError",
	"NotReadableError",
	"NotSupportedError",
	"OperationError",
	"OptOutError",
	"OverconstrainedError",
	"QuotaExceededError",
	"ReadOnlyError",
	"SecurityError",
	"TimeoutError",
	"TransactionInactiveError",
	"URLMismatchError",
	"UnknownError",
	"VersionError",
	"WrongDocumentError",
	// Synthetic names for browser-generated message-only reports (see messageOnlyErrorIdentityOf)
	"ResizeObserverLoopError",
	"CrossOriginScriptError",
	// Third-party classes the SPAs can catch
	"ErrorInfo", // ably-js
	"ZodError",
	// @pandas/api-sdk/errors
	"APIError",
	"AuthError",
	"ConflictError",
	"ForbiddenError",
	"RateLimitError",
	"ServerError",
	"ValidationError",
	// apps/vision-ai-v2 error classes
	"CameraPermissionDeniedError",
	"CaptureAckTimeoutError",
	"DegenerateSourceFrameError",
	"DetectorRoiTimeoutError",
	"FrameEncodingUnavailableError",
	"GradeResponseTimeoutError",
	"SignatureEncodingUnavailableError",
	"TransientUploadError",
	"UnsupportedUploadTypeError",
]);

const MAX_ERROR_MESSAGE_CHARS = 200;

/** The bounded name + allowlist-gated message pair, derived from one `name` read. */
export type ErrorIdentity = { errorName: string; errorMessage: string | null };

/** Bounded message read with its own failure isolation — a hostile `message` getter must not downgrade the (already-vetted) name. */
function boundedMessageOf(error: object): string | null {
	try {
		const message = (error as { message?: unknown }).message;
		if (typeof message !== "string" || message.length === 0) {
			return null;
		}
		return message.length > MAX_ERROR_MESSAGE_CHARS ? `${message.slice(0, MAX_ERROR_MESSAGE_CHARS - 1)}…` : message;
	} catch {
		return null;
	}
}

/**
 * The atomic identity read — both fields derive from a single read of `name`.
 * Never pair `errorNameOf` with `errorMessageOf`: a stateful `name` getter could
 * answer differently each call, unlocking a message under a collapsed fallback name.
 * The message ships (capped, see {@link MAX_ERROR_MESSAGE_CHARS}) only for an allowlisted `name`. Never throws.
 */
export function errorIdentityOf(error: unknown): ErrorIdentity {
	try {
		// Primitive/null throwables collapse to the fallback too — the emitted
		// name vocabulary is exactly the allowlist plus the two fallback constants.
		if (error === null || (typeof error !== "object" && typeof error !== "function")) {
			return { errorName: "UnknownError", errorMessage: null };
		}
		const name = (error as { name?: unknown }).name;
		if (typeof name === "string" && ALLOWED_ERROR_NAMES.has(name)) {
			return { errorName: name, errorMessage: boundedMessageOf(error) };
		}
		return { errorName: error instanceof Error ? "Error" : "UnknownError", errorMessage: null };
	} catch {
		return { errorName: "UnknownError", errorMessage: null };
	}
}

const MESSAGE_ERROR_NAME = /^(?:Uncaught\s+)?([A-Za-z]*(?:Error|Exception))\b/;

/**
 * Classifies an `error` event that carries no Error object (ResizeObserver loop reports, cross-origin
 * "Script error.", Worker-propagated `Uncaught <Name>: ...`). Only the leading name token is read and
 * the allowlist decides; `errorMessage` is always null because the browser text is not allowlist-gated prose. Never throws.
 */
export function messageOnlyErrorIdentityOf(message: unknown): ErrorIdentity {
	try {
		if (typeof message !== "string" || message.length === 0) {
			return { errorName: "UnknownError", errorMessage: null };
		}
		if (message.startsWith("ResizeObserver loop")) {
			return { errorName: "ResizeObserverLoopError", errorMessage: null };
		}
		if (message.startsWith("Script error")) {
			return { errorName: "CrossOriginScriptError", errorMessage: null };
		}
		const token = MESSAGE_ERROR_NAME.exec(message)?.[1];
		if (token === undefined) {
			return { errorName: "UnknownError", errorMessage: null };
		}
		return { errorName: errorIdentityOf({ name: token }).errorName, errorMessage: null };
	} catch {
		return { errorName: "UnknownError", errorMessage: null };
	}
}

/** Name-only view of {@link errorIdentityOf} — for call sites that ship no message. */
export function errorNameOf(error: unknown): string {
	return errorIdentityOf(error).errorName;
}

/**
 * Message-only view of {@link errorIdentityOf}. Safe standalone (the gate and
 * the message come from one identity read) — but never pair it with a separate
 * `errorNameOf` call in the same log line; use {@link errorIdentityOf}.
 */
export function errorMessageOf(error: unknown): string | null {
	return errorIdentityOf(error).errorMessage;
}
